import { BoardUserData } from '../components/up-board-editor/up-board-editor.component';
import { Employee } from '../components/up-board/up-board.component';
import {
  RawEmployee,
  NormalizedEmployee,
  ChannelData,
  GlobalState,
  InstoreState,
  PhoneOnlineState,
  ChannelType,
  ChannelStateValue,
} from './employee.model';

/**
 * Normalizes an employee from either legacy or new format to the multi-channel structure.
 *
 * Legacy format detection: Presence of `state` or `order` at root level without `channels`.
 *
 * Normalization rules (CR-P1-002 Auto-Upgrade Pattern):
 * - Legacy `state === 'break'` → `globalState: 'break'`, `channels.instore.state: 'inactive'`
 * - Legacy `state === 'up'` or `'onDeck'` → `channels.instore.state: 'available'` (C9 normalization)
 * - Legacy employees default to `instore.eligible: true`, `phone.eligible: false`, `online.eligible: false`
 * - Already-normalized employees are returned unchanged (idempotent)
 *
 * @param raw - Employee data in either legacy or new format
 * @returns Normalized employee with guaranteed `channels` structure
 */
export function normalizeEmployee(raw: RawEmployee): NormalizedEmployee {
  // Already normalized format (has channels property)
  if (raw.channels) {
    const globalState = raw.globalState ?? null;
    const channels = raw.channels;

    // Note: Per-channel 'inactive' is no longer valid - inactive is global only.
    // Any legacy per-channel inactive should have been migrated to globalState.

    return {
      email: raw.email,
      name: raw.name,
      phone: raw.phone,
      photoUrl: raw.photoUrl,
      roles: raw.roles,
      smsNotify: raw.smsNotify,
      globalState,
      channels,
    };
  }

  // Legacy format - normalize at runtime
  const legacyState = raw.state || 'inactive';
  const legacyOrder = raw.order ?? 0;

  // C9: Map 'up' and 'onDeck' to 'available'
  const normalizedState = (legacyState === 'up' || legacyState === 'onDeck')
    ? 'available'
    : legacyState;

    // MIGRATION: Extract global state (break OR inactive)
    // - Legacy 'break' → globalState: 'break'
    // - Legacy 'inactive' → globalState: 'inactive' (new migration for Story 5.2)
    let globalState: GlobalState = null;
    if (legacyState === 'break') {
      globalState = 'break';
    } else if (legacyState === 'inactive') {
      globalState = 'inactive';
    }

    // Determine instore state based on global state
    // - On break: instore.state = 'break' (active but temporarily unavailable)
    // - Inactive: instore.state = 'available' (preserves position for when they return)
    // - Other: use normalized state
    let instoreState: InstoreState;
    if (legacyState === 'break') {
      instoreState = 'break';
    } else if (legacyState === 'inactive') {
      instoreState = 'available'; // Global inactive, channel set to available
    } else {
      instoreState = normalizedState as InstoreState;
    }

  // For non-instore channels:
  // - If globally inactive, set channel state to 'available' (ready when they return)
  // - If not globally inactive, set to 'available' (eligible:false controls availability)
  // This avoids per-channel 'inactive' which would trigger migration on re-read
  const nonInstoreState: PhoneOnlineState = 'available';

  const channels: ChannelData = {
    instore: {
      state: instoreState,
      order: legacyOrder,
      eligible: true, // Legacy employees always eligible for instore
    },
    phone: {
      state: nonInstoreState,
      order: 0,
      eligible: false, // Legacy employees not eligible for phone (opt-in)
    },
    online: {
      state: nonInstoreState,
      order: 0,
      eligible: false, // Legacy employees not eligible for online (opt-in)
    },
  };

  return {
    email: raw.email,
    name: raw.name,
    phone: raw.phone,
    photoUrl: raw.photoUrl,
    roles: raw.roles,
    smsNotify: raw.smsNotify,
    globalState,
    channels,
  };
}

/**
 * Converts a normalized employee to the format for Firestore write.
 * Always outputs NEW format only - no legacy `state` or `order` fields.
 *
 * This is the write-side of the auto-upgrade pattern (CR-P1-002):
 * First write after normalization upgrades the employee to new format permanently.
 *
 * IMPORTANT: Firestore behavior with undefined/null values:
 * - Firestore does not accept `undefined` values in documents
 * - With merge:true, explicit `null` DELETES the field
 * - Solution: Omit undefined/null fields entirely so merge preserves existing values
 *
 * @param employee - Normalized employee to prepare for write
 * @returns Object suitable for Firestore document update (new format only)
 */
export function denormalizeForWrite(employee: NormalizedEmployee): any {
  const result: any = {
    email: employee.email,
    globalState: employee.globalState,
    channels: employee.channels,
    // CRITICAL: Do NOT write 'state' or 'order' (legacy fields removed)
  };

  // Only include fields with actual values (not undefined/null)
  // This prevents merge:true from deleting existing fields
  if (employee.name !== undefined && employee.name !== null) {
    result.name = employee.name;
  }
  if (employee.phone !== undefined && employee.phone !== null) {
    result.phone = employee.phone;
  }
  if (employee.photoUrl !== undefined && employee.photoUrl !== null) {
    result.photoUrl = employee.photoUrl;
  }
  if (employee.roles !== undefined && employee.roles !== null) {
    result.roles = employee.roles;
  }
  if (employee.smsNotify !== undefined && employee.smsNotify !== null) {
    result.smsNotify = employee.smsNotify;
  }

  return result;
}

// ============================================
// CHANNEL STATE SCOPE RULES (Task 3)
// ============================================

/**
 * Valid states for instore channel.
 * Includes delivery states which are ONLY valid for instore.
 * Note: 'inactive' is now a global state only (see GlobalState type).
 */
const INSTORE_VALID_STATES: readonly InstoreState[] = [
  'available',
  'with_guest',
  'with_apt',
  'with_guest_delivery',
  'with_apt_delivery',
  'break',
] as const;

/**
 * Valid states for phone and online channels.
 * NO appointments or deliveries allowed - these are physical instore operations.
 * Note: 'inactive' is now a global state only (see GlobalState type).
 */
const PHONE_ONLINE_VALID_STATES: readonly PhoneOnlineState[] = [
  'available',
  'with_guest',
] as const;

/**
 * States that indicate the employee is busy with a customer.
 * Used to determine cross-channel unavailability.
 */
const BUSY_STATES: readonly string[] = [
  'with_guest',
  'with_apt',
  'with_guest_delivery',
  'with_apt_delivery',
] as const;

/**
 * Checks if an employee is on global break.
 * When on break, employee is unavailable for ALL channels.
 *
 * @param employee - Normalized employee to check
 * @returns true if employee is on global break
 */
export function isEmployeeOnBreak(employee: NormalizedEmployee): boolean {
  return employee.globalState === 'break';
}

/**
 * Checks if an employee is globally inactive.
 * When inactive, employee is unavailable for ALL channels (off-duty/end of day).
 *
 * @param employee - Normalized employee to check
 * @returns true if employee is globally inactive
 */
export function isEmployeeInactive(employee: NormalizedEmployee): boolean {
  return employee.globalState === 'inactive';
}

/**
 * Checks if an employee is globally unavailable (break OR inactive).
 * Use this for any "skip this employee" logic in queue processing.
 *
 * An employee is globally unavailable when:
 * - On break (temporary unavailability)
 * - Inactive (off-duty/end of day)
 *
 * @param employee - Normalized employee to check
 * @returns true if employee is globally unavailable
 */
export function isEmployeeGloballyUnavailable(employee: NormalizedEmployee): boolean {
  return employee.globalState === 'break' || employee.globalState === 'inactive';
}

/**
 * Gets the valid states for a given channel.
 *
 * Channel State Scope Rules:
 * - Instore: All states including appointments and deliveries
 * - Phone/Online: Only 'available', 'with_guest', 'inactive' (no physical operations)
 *
 * @param channel - The channel to get valid states for
 * @returns Array of valid state values for the channel
 */
export function getValidStatesForChannel(channel: ChannelType): readonly ChannelStateValue[] {
  if (channel === 'instore') {
    return INSTORE_VALID_STATES;
  }
  return PHONE_ONLINE_VALID_STATES;
}

/**
 * Validates if a state is valid for a given channel.
 *
 * @param state - The state to validate
 * @param channel - The channel to validate against
 * @returns true if state is valid for the channel
 */
export function isValidStateForChannel(state: string, channel: ChannelType): boolean {
  const validStates = getValidStatesForChannel(channel);
  return validStates.includes(state as ChannelStateValue);
}

/**
 * Checks if an employee is busy in any channel.
 * Used to enforce cross-channel unavailability rules.
 *
 * Business Rule: If busy on any channel (with_guest, with_apt, delivery states),
 * the employee is unavailable for leads in other channels.
 *
 * @param employee - Normalized employee to check
 * @returns true if employee is busy in any channel
 */
export function isEmployeeBusyInAnyChannel(employee: NormalizedEmployee): boolean {
  const { channels } = employee;
  return (
    BUSY_STATES.includes(channels.instore.state) ||
    BUSY_STATES.includes(channels.phone.state) ||
    BUSY_STATES.includes(channels.online.state)
  );
}

// ============================================
// MULTI-CHANNEL HELPER FUNCTIONS (Task 4)
// ============================================

/**
 * Gets employees available for a specific channel, sorted by queue order.
 * Works with both old and new data formats via normalize-first pattern.
 *
 * Availability requirements:
 * - Employee is eligible for the channel
 * - Employee's channel state is 'available'
 * - Employee is NOT on global break
 * - Employee is NOT busy in any other channel
 *
 * @param users - Array of raw employee data (may be mixed formats)
 * @param channel - The channel to get available employees for
 * @returns Array of normalized employees available for the channel, sorted by order
 */
export function getAvailableForChannel(users: RawEmployee[], channel: ChannelType): NormalizedEmployee[] {
  return users
    .map(normalizeEmployee)
    .filter((emp) => {
      // Must be eligible for this channel
      if (!emp.channels[channel].eligible) return false;
      // Must not be on global break
      if (isEmployeeOnBreak(emp)) return false;
      // Must be 'available' in this channel
      if (emp.channels[channel].state !== 'available') return false;
      // Must not be busy in any channel (cross-channel block)
      if (isEmployeeBusyInAnyChannel(emp)) return false;
      return true;
    })
    .sort((a, b) => a.channels[channel].order - b.channels[channel].order);
}

/**
 * Calculates the "Up" and "On Deck" employees for a specific channel.
 * Up = next employee to receive a lead (order position 1)
 * On Deck = second in line (order position 2)
 *
 * Works with both old and new data formats via normalize-first pattern.
 *
 * @param users - Array of raw employee data (may be mixed formats)
 * @param channel - The channel to calculate for
 * @returns Object with up and onDeck employees (may be undefined if queue is empty/short)
 */
export function calculateUpAndOnDeck(
  users: RawEmployee[],
  channel: ChannelType
): { up: NormalizedEmployee | undefined; onDeck: NormalizedEmployee | undefined } {
  const available = getAvailableForChannel(users, channel);
  return {
    up: available[0],
    onDeck: available[1],
  };
}

/**
 * Gets employees currently busy with a customer in a specific channel.
 * Works with both old and new data formats via normalize-first pattern.
 *
 * @param users - Array of raw employee data (may be mixed formats)
 * @param channel - The channel to get busy employees for
 * @returns Array of normalized employees busy in the channel
 */
export function getBusyWithChannel(users: RawEmployee[], channel: ChannelType): NormalizedEmployee[] {
  return users
    .map(normalizeEmployee)
    .filter((emp) => {
      const channelState = emp.channels[channel].state;
      return BUSY_STATES.includes(channelState);
    });
}

/**
 * Checks if an employee is unavailable for a specific channel.
 * Used for cross-channel busy blocking.
 *
 * An employee is unavailable when:
 * - Not eligible for the channel
 * - On global break
 * - Busy in ANY channel (cross-channel block)
 * - Channel state is 'inactive'
 *
 * @param employee - Normalized employee to check
 * @param channel - The channel to check availability for
 * @returns true if employee is unavailable for the channel
 */
export function isEmployeeUnavailable(employee: NormalizedEmployee, channel: ChannelType): boolean {
  // Not eligible for this channel
  if (!employee.channels[channel].eligible) return true;
  // On global break
  if (isEmployeeOnBreak(employee)) return true;
  // Story 5.3: Check global inactive state
  if (isEmployeeInactive(employee)) return true;
  // Busy in any channel (cross-channel block)
  if (isEmployeeBusyInAnyChannel(employee)) return true;
  return false;
}

/**
 * Normalizes an array of raw employees.
 * Convenience function for batch normalization.
 *
 * @param users - Array of raw employee data (may be mixed formats)
 * @returns Array of normalized employees
 */
export function normalizeEmployees(users: RawEmployee[]): NormalizedEmployee[] {
  return users.map(normalizeEmployee);
}

/**
 * Helper: Extracts state from user data in either legacy or new format.
 * Handles state equivalence mapping:
 * - Legacy 'up'/'onDeck' → 'available'
 * - Legacy 'break' → globalState === 'break'
 *
 * For new format with globalState === 'break', returns 'break' regardless of instore.state.
 * For legacy format, returns the state directly (already normalized by normalizeEmployee).
 *
 * @param user - User data in either legacy or new format
 * @returns Normalized state string
 */
function getEmployeeState(user: BoardUserData | any): string {
  // Check if this is new format (has channels property)
  if (user.channels) {
    // New format: Check globalState for break first
    if (user.globalState === 'break') {
      return 'break';
    }
    // Otherwise return the instore state
    return user.channels.instore.state || 'inactive';
  }

  // Legacy format: state exists at root level
  // Normalize legacy states according to C9 mapping
  const legacyState = user.state || 'inactive';

  // Map legacy 'up' and 'onDeck' to 'available' for consistency
  if (legacyState === 'up' || legacyState === 'onDeck') {
    return 'available';
  }

  return legacyState;
}

/**
 * Helper: Extracts order from user data in either legacy or new format.
 *
 * @param user - User data in either legacy or new format
 * @returns Queue order number
 */
function getEmployeeOrder(user: BoardUserData | any): number {
  // Check if this is new format
  if (user.channels) {
    return user.channels.instore.order ?? 0;
  }

  // Legacy format
  return user.order ?? 0;
}

/**
 * Filters employees by state with dual-format support.
 * Works with both legacy (state/order at root) and new format (channels.instore).
 *
 * Handles state equivalence:
 * - 'up'/'onDeck' in legacy data are treated as equivalent to 'available'
 * - 'break' is identified by globalState === 'break' in new format
 *
 * @param users - Array of user data (may be mixed formats)
 * @param state - Target state to match
 * @param includeState - If true, matches users IN the state; if false, matches users NOT IN the state
 * @returns Array of employees matching the state filter, sorted by order
 */
function filterUsersByState(
  users: BoardUserData[],
  state: string,
  includeState: boolean
): Employee[] {
  return users
    .filter((u) => {
      // Must be a salesman
      if (!u.roles?.salesman) {
        return false;
      }

      const userState = getEmployeeState(u);

      // Apply the state filter
      if (includeState) {
        return userState === state;
      } else {
        return userState !== state;
      }
    })
    .map((u) => {
      const normalizedState = getEmployeeState(u);
      // Map normalized 'available' back to 'up' for UI backward compatibility
      // This preserves AC-4 from Story 1.7: "No changes required to existing component code"
      const uiState = normalizedState === 'available' ? 'up' : normalizedState;

      return {
        email: u.email,
        name: u.name,
        phone: u.phone,
        photoUrl: u.photoUrl,
        state: uiState,
        order: getEmployeeOrder(u),
      } as Employee;
    })
    .sort((a, b) => {
      return a.order > b.order ? 1 : -1;
    });
}

/**
 * Gets employees currently in a specific state.
 * Supports both legacy and new multi-channel data formats.
 *
 * State Equivalence (from Story 1.5):
 * - Legacy 'up'/'onDeck' are normalized to 'available'
 * - Legacy 'break' is mapped to 'break' (stored in globalState for new format)
 *
 * During transition period, both formats work seamlessly:
 * - Legacy data: Uses root-level `state` field
 * - New format: Uses `channels.instore.state` (with globalState override for break)
 * - Mixed arrays: Both formats work together without issues
 *
 * AC Requirements:
 * - AC-1: Existing queries for state field still work with migrated data
 * - AC-2: Helper functions work with both legacy and migrated data
 * - AC-4: No changes required to existing component code
 *
 * @param users - Array of user data (may contain legacy, new, or mixed formats)
 * @param state - Target state to filter for (e.g., 'available', 'break', 'with_guest')
 * @returns Array of employees in the specified state, sorted by order (ascending)
 */
export function getUsersInState(
  users: BoardUserData[],
  state: string
): Employee[] {
  return filterUsersByState(users, state, true);
}

/**
 * Gets employees NOT in a specific state.
 * Supports both legacy and new multi-channel data formats.
 *
 * State Equivalence (from Story 1.5):
 * - Legacy 'up'/'onDeck' are normalized to 'available'
 * - Legacy 'break' is mapped to 'break' (stored in globalState for new format)
 * - Employees on break should NOT match 'available' queries
 *
 * During transition period, both formats work seamlessly:
 * - Legacy data: Uses root-level `state` field
 * - New format: Uses `channels.instore.state` (with globalState override for break)
 * - Mixed arrays: Both formats work together without issues
 *
 * AC Requirements:
 * - AC-1: Existing queries for state field still work with migrated data
 * - AC-2: Helper functions work with both legacy and migrated data
 * - AC-4: No changes required to existing component code
 *
 * @param users - Array of user data (may contain legacy, new, or mixed formats)
 * @param state - Target state to exclude from results (e.g., 'available', 'break')
 * @returns Array of employees NOT in the specified state, sorted by order (ascending)
 */
export function getUsersNotInState(
   users: BoardUserData[],
   state: string
): Employee[] {
   return filterUsersByState(users, state, false);
}

// ============================================
// STORY 2.4: CHANNEL-SPECIFIC HELPER FUNCTIONS
// ============================================

/**
 * Returns all users in a specified state for a given channel.
 * Works with both multi-channel and legacy single-channel data.
 *
 * Implementation Notes:
 * - For multi-channel format: checks `channels[channel].state === state`
 * - For legacy format: if no channel specified, checks root `state` field
 * - Filters to only users with salesman role
 * - Returns sorted by channel order (ascending)
 *
 * AC-1: Returns users in specified state for channel
 * AC-9: Includes JSDoc documentation
 *
 * @param users - Array of user data (may be mixed formats)
 * @param state - Target state (e.g., 'available', 'with_guest', 'inactive')
 * @param channel - Channel name (e.g., 'instore', 'phone', 'online'), defaults to 'instore'
 * @returns Array of users in the specified state for that channel, sorted by order
 *
 * @example
 * const inUpQueue = getUsersInStateForChannel(employees, 'available', 'instore');
 * const phoneWithGuests = getUsersInStateForChannel(employees, 'with_guest', 'phone');
 */
export function getUsersInStateForChannel(
   users: BoardUserData[],
   state: string,
   channel: string = 'instore'
): Employee[] {
   return users
      .filter((u) => {
         // Must be a salesman
         if (!u.roles?.salesman) {
            return false;
         }

         // Check if this is new format with channels
         if (u.channels) {
            // New format: check the specific channel state
            const channelData = (u.channels as any)[channel];
            if (!channelData) return false;
            return channelData.state === state;
         }

         // Legacy format: only check instore (default channel)
         if (channel === 'instore') {
            const userState = getEmployeeState(u);
            return userState === state;
         }

         // Legacy format doesn't support other channels
         return false;
      })
      .map((u) => {
         const normalizedState = getEmployeeState(u);
         // Map normalized 'available' back to 'up' for UI backward compatibility
         const uiState = normalizedState === 'available' ? 'up' : normalizedState;

         return {
            email: u.email,
            name: u.name,
            phone: u.phone,
            photoUrl: u.photoUrl,
            state: uiState,
            order: getEmployeeOrder(u),
         } as Employee;
      })
      .sort((a, b) => a.order - b.order);
}

/**
 * Returns all active (not inactive) users for a given channel.
 * Useful for queue calculations and display.
 *
 * Active = not in 'inactive' state AND eligible for the channel.
 * Works with both multi-channel and legacy single-channel data.
 *
 * Implementation Notes:
 * - Filters out users with state === 'inactive'
 * - Checks `channels[channel].eligible` flag
 * - Returns in queue order (sorted by `channels[channel].order`)
 * - Filters to only users with salesman role
 *
 * AC-2: Returns users not in inactive state for channel
 * AC-9: Includes JSDoc documentation
 *
 * @param users - Array of user data (may be mixed formats)
 * @param channel - Channel name (e.g., 'instore', 'phone', 'online'), defaults to 'instore'
 * @returns Array of active users for that channel, sorted by order
 *
 * @example
 * const inStoreActive = getActiveUsersForChannel(employees, 'instore');
 * const phoneActive = getActiveUsersForChannel(employees, 'phone');
 */
export function getActiveUsersForChannel(
    users: BoardUserData[],
    channel: string = 'instore'
): Employee[] {
    return users
       .filter((u) => {
          // Must be a salesman
          if (!u.roles?.salesman) {
             return false;
          }

          // Check if this is new format with channels
          if (u.channels) {
             // Story 5.3: Check for global break OR global inactive
             if (u.globalState === 'break' || u.globalState === 'inactive') {
                return false;
             }
             const channelData = (u.channels as any)[channel];
             if (!channelData) return false;
             // Must be eligible for this channel
             if (!channelData.eligible) return false;
             // Channel state is no longer 'inactive' for globally inactive employees
             // (Story 5.3 ensures channel states stay 'available' when going inactive)
             return true;
          }

          // Legacy format: only supports instore channel
          if (channel === 'instore') {
             const userState = getEmployeeState(u);
             // Active if not inactive and not on break (break is a global state)
             return userState !== 'inactive' && userState !== 'break';
          }

          // Legacy format doesn't support other channels
          return false;
       })
       .map((u) => {
          const normalizedState = getEmployeeState(u);
          // Map normalized 'available' back to 'up' for UI backward compatibility
          const uiState = normalizedState === 'available' ? 'up' : normalizedState;

          return {
             email: u.email,
             name: u.name,
             phone: u.phone,
             photoUrl: u.photoUrl,
             state: uiState,
             order: getEmployeeOrder(u),
          } as Employee;
       })
       .sort((a, b) => a.order - b.order);
}

/**
 * Returns the ordered queue (all active users) for a given channel.
 * This is the base queue from which "up" and "on deck" are calculated.
 *
 * Queue = all active users sorted by order value (ascending).
 * Synonym for `getActiveUsersForChannel` but used in explicit queue context.
 *
 * Implementation Notes:
 * - Delegates to `getActiveUsersForChannel` for consistency
 * - Must be sorted by `channels[channel].order` ascending
 * - Preserves order calculation logic from Story 2.2
 *
 * AC-3: Returns ordered queue for channel
 * AC-9: Includes JSDoc documentation
 *
 * @param users - Array of user data (may be mixed formats)
 * @param channel - Channel name (e.g., 'instore', 'phone', 'online'), defaults to 'instore'
 * @returns Ordered array of active users for that channel (queue)
 *
 * @example
 * const queue = getUsersInQueue(employees, 'instore');
 * const up = queue[0];
 * const onDeck = queue[1];
 */
export function getUsersInQueue(
   users: BoardUserData[],
   channel: string = 'instore'
): Employee[] {
   return getActiveUsersForChannel(users, channel);
}

/**
 * Checks if a user is available for a specific channel.
 * Availability = not busy on ANY channel AND not in global break.
 *
 * An employee is available for a channel when:
 * 1. NOT on global break (globalState !== 'break')
 * 2. NOT busy on ANY channel (Story 2.3 busy detection)
 * 3. Channel-eligible (story 2.5 context, but checked here)
 * 4. Channel state is 'available' (for new format) or 'up'/'available' (legacy)
 *
 * Works with both multi-channel and legacy single-channel data.
 *
 * Implementation Notes:
 * - Uses busy-state detection from Story 2.3 (BUSY_STATES)
 * - Checks `channels[channel].eligible` flag
 * - For legacy data, maps 'up'/'onDeck' to 'available'
 *
 * AC-4: Checks cross-channel availability
 * AC-9: Includes JSDoc documentation
 *
 * @param user - User to check
 * @param channel - Target channel (e.g., 'instore', 'phone', 'online'), defaults to 'instore'
 * @returns true if user is available for that channel
 *
 * @example
 * if (isUserAvailableForChannel(emp, 'instore')) {
 *   // Employee can receive an instore lead
 * }
 */
export function isUserAvailableForChannel(
   user: BoardUserData,
   channel: string = 'instore'
): boolean {
   // Check if this is new format with channels
   if (user.channels) {
      // Global break blocks all channels
      if (user.globalState === 'break') {
         return false;
      }

      const channelData = (user.channels as any)[channel];
      if (!channelData) return false;

      // Must be eligible for this channel
      if (!channelData.eligible) return false;

      // Must not be busy on ANY channel (cross-channel block)
      // Check all channels for busy states
      for (const ch of ['instore', 'phone', 'online']) {
         const chData = (user.channels as any)[ch];
         if (BUSY_STATES.includes(chData?.state)) {
            return false;
         }
      }

      // Must be available in this channel
      return channelData.state === 'available';
   }

   // Legacy format
   if (channel !== 'instore') {
      // Legacy format doesn't support other channels
      return false;
   }

    const userState = getEmployeeState(user);
    // Available if state is 'up' or 'available' and not on break
    return userState === 'up' || userState === 'available';
}

/**
 * Returns the global state of a user (affects all channels).
 * Global state is a single field that impacts ALL channels simultaneously.
 *
 * Only valid global state value is 'break' (means on break for all channels).
 * Null/undefined means no global state is set.
 *
 * Implementation Notes:
 * - Returns `user.globalState` directly
 * - Null/undefined treated as "no global state"
 * - Only valid value: 'break'
 * - Used by Story 2.1 for break state detection
 * - Used by Story 2.3 for cross-channel availability
 *
 * AC-5: Returns global state or null
 * AC-9: Includes JSDoc documentation
 *
 * @param user - User to check
 * @returns Global state ('break' or null/undefined)
 *
 * @example
 * const state = getGlobalState(emp);
 * if (state === 'break') {
 *   // Employee is on break across all channels
 * }
 */
export function getGlobalState(user: BoardUserData): string | null {
   // Return globalState if it exists, otherwise null
   if (user.globalState === 'break') {
      return 'break';
   }
   return null;
}

/**
 * Returns true if user is in global break state.
 * Convenience function that wraps `getGlobalState()`.
 *
 * When a user is on break, they are unavailable for ALL channels.
 * This is a global state, not per-channel.
 *
 * Implementation Notes:
 * - Wrapper around `getGlobalState(user) === 'break'`
 * - Simplifies common "is user on break?" checks
 *
 * AC-6: Returns true if in global break state
 * AC-9: Includes JSDoc documentation
 *
 * @param user - User to check
 * @returns true if user.globalState === 'break'
 *
 * @example
 * if (isUserOnBreak(emp)) {
 *   skipEmployee(); // Don't assign leads
 * }
 */
export function isUserOnBreak(user: BoardUserData): boolean {
   return getGlobalState(user) === 'break';
}

/**
 * Clears the global state for a user (e.g., returning from break).
 * Returns a NEW user object with globalState set to null (immutable pattern).
 *
 * This function follows the immutable pattern used throughout the codebase:
 * - Does NOT modify the input user object
 * - Returns a new object with updated globalState
 * - All other fields preserved unchanged
 *
 * Implementation Notes:
 * - Returns a new object (immutable pattern per Story 2.1-2.3)
 * - Sets `globalState` to null
 * - Does NOT modify channel-specific states
 * - Called when user transitions from break via `enterQueue()`
 *
 * AC-7: Clears global state field
 * AC-9: Includes JSDoc documentation
 *
 * @param user - User to modify
 * @returns NEW user object with globalState cleared (set to null)
 *
 * @example
 * const employee = { globalState: 'break', channels: {...} };
 * const cleared = clearGlobalState(employee);
 * // employee is unchanged, cleared has globalState = null
 */
export function clearGlobalState(user: BoardUserData): BoardUserData {
   // Return a new object with globalState cleared
   // Spread operator creates shallow copy; globalState is a primitive so deep copy not needed
   return {
      ...user,
      globalState: null,
   };
}
